import { createApp, watch } from 'vue';
import { createPinia } from 'pinia';
import '@fontsource-variable/inter';
import '@fontsource-variable/fraunces/opsz.css';
import './design/styles/index.css';
import App from './App.vue';
import { createAppRouter } from './app/router.ts';
import { createApi } from './app/api/index.ts';
import { HttpClient } from './app/http/HttpClient.ts';
import { RealtimeClient } from './app/realtime/RealtimeClient.ts';
import { CollabClient } from './app/collab/CollabClient.ts';
import { PushClient, registerServiceWorker } from './app/push/PushClient.ts';
import { listenForInstall } from './app/composables/useInstallPrompt.ts';
import { useSessionStore } from './app/stores/session.ts';
import { useWorkspaceStore } from './app/stores/workspace.ts';
import { useInboxStore } from './app/stores/inbox.ts';
import { PausedRedirect } from './app/PausedRedirect.ts';

const pinia = createPinia();
const realtime = new RealtimeClient();
const collab = new CollabClient();
const push = new PushClient();
// Installable app and push notifications (the worker does nothing else).
registerServiceWorker();
listenForInstall();
const api = createApi({ http: new HttpClient({ clientId: realtime.clientId }) });
const session = useSessionStore(pinia);
session.bind(api.auth);
useWorkspaceStore(pinia).bind(api);
useInboxStore(pinia).bind(api);
const router = createAppRouter(pinia);

// A session that ends mid-use (expired, revoked elsewhere) sends the user to log in.
api.http.onUnauthorized = () => {
  if (!session.isAuthenticated) return;
  session.expire();
  void router.replace({ name: 'login', query: { next: router.currentRoute.value.fullPath } });
};

// Paused mid-use (the owner's plan): the page moves to the paused one.
const pausedRedirect = new PausedRedirect({
  router,
  session,
  workspace: useWorkspaceStore(pinia),
});
api.http.onAccessPaused = () => void pausedRedirect.handle();

// Live updates while someone is signed in.
watch(
  () => session.isAuthenticated,
  (signedIn) => {
    if (signedIn) return realtime.connect();
    realtime.disconnect();
    collab.disconnect();
  },
  { immediate: true },
);

createApp(App).use(pinia).use(api).use(realtime).use(collab).use(push).use(router).mount('#app');
